/**
 * Adapts the floating sticks + gyro into the sim's `SimInput`. The left stick
 * is run (x) + jump (flick up) + crouch (hold down); the right stick is a
 * render-only camera peek (never enters the sim); interact is an edge set by a
 * HUD button. Edges (jump/interact) fire once per frame and are cleared by the
 * fixed-step loop so they cannot double-fire across sub-steps.
 */
import type { SimInput } from '../sim/types.js';
import type { Joystick } from './touchControls.js';
import type { DeviceTilt } from './useDeviceTilt.js';

const DEADZONE = 0.15;
const JUMP_UP = 0.55; // stick-up magnitude that counts as a jump flick
const CROUCH_DOWN = 0.5;

function deadzone(v: number): number {
  if (Math.abs(v) < DEADZONE) return 0;
  const s = Math.sign(v);
  return s * ((Math.abs(v) - DEADZONE) / (1 - DEADZONE));
}

export interface SimInputSource {
  /** Current input for this frame (a stable, mutated object). */
  sample(): SimInput;
  /** Clear edge-triggers after a fixed step consumes them. */
  clearEdges(): void;
  /** Queue an interact press (called by the HUD button). */
  pressInteract(): void;
  /** Queue a jump press (called by the JUMP button). */
  pressJump(): void;
  /** Hold/release crouch (called by the CROUCH button). */
  setCrouch(held: boolean): void;
  /** Right-stick vector for camera peek (render-only). */
  look(): { x: number; y: number };
}

export function createSimInput(
  moveStick: Joystick,
  lookStick: Joystick,
  tilt: DeviceTilt,
): SimInputSource {
  const input: SimInput = { steer: 0, jump: false, crouch: false, interact: false };
  let prevUp = false;
  let interactQueued = false;
  let jumpQueued = false;
  let crouchButton = false;

  return {
    sample() {
      const m = moveStick.value;
      // Steer: stick x plus a gentle gyro nudge.
      input.steer = Math.max(-1, Math.min(1, deadzone(m.x) + tilt.value.x * 0.3));
      input.crouch = m.y > CROUCH_DOWN || crouchButton;
      const up = m.y < -JUMP_UP; // y is down-positive, so up is negative
      input.jump = (up && !prevUp) || jumpQueued; // flick-up rising edge OR button
      prevUp = up;
      jumpQueued = false;
      input.interact = interactQueued;
      interactQueued = false;
      return input;
    },
    clearEdges() {
      input.jump = false;
      input.interact = false;
    },
    pressInteract() {
      interactQueued = true;
    },
    pressJump() {
      jumpQueued = true;
    },
    setCrouch(held) {
      crouchButton = held;
    },
    look() {
      return lookStick.value;
    },
  };
}
